import { usePersistedState } from '@/shared/hooks/usePersistedState';
import { ALL_UNITS } from '@/shared/utils/unitFilter';

import {
  type CommercialWidgetId,
  DEFAULT_ACTIVITY_WIDGETS,
  DEFAULT_SUMMARY_WIDGETS,
  isCommercialWidgetId,
} from '../config/widgets';
import {
  DEFAULT_SCORECARD_COLUMNS,
  isScorecardColumnId,
  type ScorecardColumnId,
} from '../config/scorecardColumns';

const STORAGE_KEYS = {
  SUMMARY_WIDGETS: 'commercial_dashboard_summary_widgets',
  ACTIVITY_WIDGETS: 'commercial_dashboard_activity_widgets',
  SCORECARD_COLUMNS: 'commercial_dashboard_scorecard_columns',
  CHART_MODE: 'commercial_dashboard_chart_mode',
  COMPARISON_MODE: 'commercial_dashboard_comparison_mode',
  UNIT_FILTER: 'commercial_dashboard_unit_filter',
} as const;

/** Receita (R$) ou quantidade (vendas, leads, visitas). */
export type CommercialChartMode = 'revenue' | 'count';
/** O comparativo entre períodos em receita ou em número de vendas. */
export type CommercialComparisonMode = 'revenue' | 'deals';

/**
 * Lista salva → lista válida: só ids conhecidos, sem repetição. Lista VAZIA é
 * escolha válida (o usuário tirou todos os blocos) e é respeitada; o que não é
 * lista cai no padrão.
 */
const sanitizeIds =
  <T extends string>(isValid: (v: unknown) => v is T, fallback: T[]) =>
  (raw: unknown): T[] =>
    Array.isArray(raw) ? [...new Set(raw.filter(isValid))] : fallback;

const sanitizeChoice =
  <T extends string>(choices: readonly T[], fallback: T) =>
  (raw: unknown): T =>
    choices.includes(raw as T) ? (raw as T) : fallback;

/**
 * Preferências do dashboard de Gestão Comercial, salvas no navegador e
 * recarregadas sempre: blocos de indicadores, colunas da tabela do time,
 * modo do gráfico e do comparativo, e a unidade escolhida.
 *
 * A unidade salva é conferida pela página contra a lista de unidades — uma
 * loja excluída (ou de outra empresa, na troca de conta) volta para "Todas".
 */
export const useCommercialDashboardPreferences = () => {
  const [summaryWidgets, setSummaryWidgets] = usePersistedState<CommercialWidgetId[]>(
    STORAGE_KEYS.SUMMARY_WIDGETS,
    DEFAULT_SUMMARY_WIDGETS,
    sanitizeIds(isCommercialWidgetId, DEFAULT_SUMMARY_WIDGETS),
  );
  const [activityWidgets, setActivityWidgets] = usePersistedState<CommercialWidgetId[]>(
    STORAGE_KEYS.ACTIVITY_WIDGETS,
    DEFAULT_ACTIVITY_WIDGETS,
    sanitizeIds(isCommercialWidgetId, DEFAULT_ACTIVITY_WIDGETS),
  );
  const [scorecardColumns, setScorecardColumns] = usePersistedState<ScorecardColumnId[]>(
    STORAGE_KEYS.SCORECARD_COLUMNS,
    DEFAULT_SCORECARD_COLUMNS,
    sanitizeIds(isScorecardColumnId, DEFAULT_SCORECARD_COLUMNS),
  );
  const [chartMode, setChartMode] = usePersistedState<CommercialChartMode>(
    STORAGE_KEYS.CHART_MODE,
    'revenue',
    sanitizeChoice(['revenue', 'count'], 'revenue'),
  );
  const [comparisonMode, setComparisonMode] = usePersistedState<CommercialComparisonMode>(
    STORAGE_KEYS.COMPARISON_MODE,
    'revenue',
    sanitizeChoice(['revenue', 'deals'], 'revenue'),
  );
  const [unitFilter, setUnitFilter] = usePersistedState<string>(
    STORAGE_KEYS.UNIT_FILTER,
    ALL_UNITS,
    (raw) => (typeof raw === 'string' ? raw : ALL_UNITS),
  );

  return {
    summaryWidgets,
    setSummaryWidgets,
    activityWidgets,
    setActivityWidgets,
    scorecardColumns,
    setScorecardColumns,
    chartMode,
    setChartMode,
    comparisonMode,
    setComparisonMode,
    unitFilter,
    setUnitFilter,
  };
};
